import { all, select, put, call } from 'redux-saga/effects';
import {
  PapiDaSharedGallectionContents,
  PapiCommentThread,
  PapiRequestDaSharedGallectionContents,
} from '@wix/da-papi-types';
import { ALL_FOLDER_ID, SCRAPS_FOLDER_ID } from '@wix/da-url';
import { CommentableTypes } from '@wix/da-shared-react/pkg/Comment/redux/types';
import { normalize } from '@wix/da-shared-react/pkg/redux/normalizr';
import { deviationSchema } from '@wix/da-shared-react/pkg/redux/normalizr/schemas/deviation';
import { withOffset, selectors as streamSelectors } from '@wix/da-shared-react/pkg/Stream';
import { fetchStreamContents } from '@wix/da-shared-react/pkg/Stream/helpers';
import { actions as threadActions } from '@wix/da-shared-react/pkg/CommentThread';
import { getProfileOwner } from '../../selectors/users';
import { getFeaturedFolder } from '../../selectors/folders';
import { GallectionType } from '../../../types/gallection';
import { biFetchMoreFolderItems } from '../../actions/bi';
import { putEntities } from '@wix/da-shared-react/pkg/redux/entities/actions';
import { getSchemaForGallectionType } from '../../utils/gallection';
import { fetchNextFolderDeviations } from '../../actions/gallectionSection';

interface FolderDeviationStreamParams {
  deviationIdSeed?: number;
  effectiveOrder?: 'personalized' | 'newest' | 'oldest';
  folderId: number;
  gallectionType: GallectionType;
}

interface GallectionContentsResponse extends PapiDaSharedGallectionContents {
  comments?: PapiCommentThread;
}

function getCommentableTypeId(gallectionType: GallectionType) {
  return gallectionType === GallectionType.COLLECTION
    ? CommentableTypes.COMMENT_COLLECTION
    : CommentableTypes.COMMENT_GALLERY;
}

function* handleFetchSuccess(
  response: GallectionContentsResponse,
  streamParams: FolderDeviationStreamParams
) {
  const featuredFolder = yield select(getFeaturedFolder);
  const commentableItemId =
    !streamParams.folderId || [ALL_FOLDER_ID, SCRAPS_FOLDER_ID].includes(streamParams.folderId)
      ? featuredFolder.folderId
      : streamParams.folderId;

  if (response.gallection) {
    const gallectionSchema = getSchemaForGallectionType(streamParams.gallectionType);
    const { entities } = normalize(response.gallection, gallectionSchema);
    yield put(putEntities({ entities }));
  }

  if (!response.comments) {
    return;
  }

  yield put(
    threadActions.initializeThread({
      thread: {
        commentableTypeId: getCommentableTypeId(streamParams.gallectionType),
        commentableItemId,
        items: response.comments.thread,
        total: response.comments.total,
        hasMore: response.comments.hasMore,
        canPostComment: response.comments.canPostComment,
        cursor: response.comments.cursor,
      },
    })
  );
}

export default function* fetchMoreFolderDeviations({
  payload: { streamId },
}: ReturnType<typeof fetchNextFolderDeviations>) {
  yield put(biFetchMoreFolderItems(streamId));

  const [offset, limit, streamParams, profileOwner] = yield all([
    select(state => withOffset.selectors.getNextOffset(state, streamId)),
    select(state => streamSelectors.getItemsPerFetch(state, streamId)),
    select(state =>
      streamSelectors.getStreamParamsSafe<FolderDeviationStreamParams>(state, streamId)
    ),
    select(getProfileOwner),
  ]);

  const queryParams: PapiRequestDaSharedGallectionContents = {
    username: profileOwner.user.username,
    type: streamParams.gallectionType,
    deviationid: streamParams.deviationIdSeed,
    order:
      streamParams.effectiveOrder && streamParams.gallectionType === GallectionType.GALLERY
        ? streamParams.effectiveOrder
        : 'default',
    offset,
    limit,
  };

  if (streamParams.folderId === ALL_FOLDER_ID) {
    queryParams.all_folder = true;
  } else if (streamParams.folderId === SCRAPS_FOLDER_ID) {
    queryParams.scraps_folder = true;
  } else {
    queryParams.folderid = streamParams.folderId;
  }

  yield fetchStreamContents<PapiRequestDaSharedGallectionContents, GallectionContentsResponse>({
    streamId,
    url: 'gallection/contents',
    params: queryParams,
    getStreamData: response => {
      const { result, entities } = normalize(response.results, [deviationSchema]);
      return {
        hasMore: response.hasMore,
        nextOffset: response.nextOffset,
        items: result,
        entities,
      };
    },
    onSuccess: function* onSuccess(response) {
      yield call(handleFetchSuccess, response, streamParams);
    },
  });
}
